import { useEffect, useRef, useState } from 'react'
import type { Suggestion } from '../api'
import { api, ApiError } from '../api'
import { useStored } from '../state'
import type { Condition, Group, Node } from '../tree'
import {
  DATE_FIELDS, FIELD_LABELS, LINK_FIELDS, activeConditions, appendTo, cond, countGroups, isGroup, isRaw,
  removeNode, updateNode, valueText, withIds,
} from '../tree'

type Props = {
  tree: Group
  setTree: (t: Group | ((p: Group) => Group)) => void
  text: string
  canUseRaw: boolean
  onSave: () => void
  error: string | null
  setError: (e: string | null) => void
}

const BUILDER_FIELDS = ['text', 'person', 'gesicht', 'ort', 'schlagwort', 'sammlung', 'technik', 'fotograf', 'jahr', 'aufnahmedatum', 'titel', 'beschreibung', 'inv']

export function Formula({ tree, setTree, text, onSave, error, setError }: Props) {
  const [open, setOpen] = useStored('zint-formula-open', false)
  const [draft, setDraft] = useState(text)
  const [editing, setEditing] = useState(false)
  const area = useRef<HTMLTextAreaElement>(null)

  useEffect(() => { if (!editing) setDraft(text) }, [text, editing])
  useEffect(() => {
    const t = area.current
    if (t) { t.style.height = 'auto'; t.style.height = `${Math.max(t.scrollHeight, 34)}px` }
  }, [draft, open])

  // parse the text formula after a pause in typing
  useEffect(() => {
    if (!editing) return
    const h = setTimeout(async () => {
      if (!draft.trim()) { setTree({ op: 'and', items: [] }); setError(null); return }
      try {
        const r = await api.parse(draft)
        setTree(withIds(r.query))
        setError(null)
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Formel nicht lesbar')
      }
    }, 450)
    return () => clearTimeout(h)
  }, [draft, editing, setTree, setError])

  const n = activeConditions(tree).length
  const g = countGroups(tree)
  const summary = n
    ? `· ${n} ${n === 1 ? 'Bedingung' : 'Bedingungen'}${g ? `, ${g} ${g === 1 ? 'Gruppe' : 'Gruppen'}` : ''}${open ? '' : ' – einblenden zum Bearbeiten'}`
    : open ? '' : '· Bedingungen mit UND, ODER, NICHT kombinieren'

  return (
    <div className={`formula${open ? ' open' : ''}`}>
      <div className="formula-head">
        <button className="ftoggle" aria-expanded={open} aria-controls="fbody" onClick={() => setOpen(!open)}>
          <span className="caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
          <span className="label">Erweiterte Suche</span>
          <span className="fsum">{summary}</span>
        </button>
        <div className="tools">
          {open && <button className="linkbtn" onClick={() => setTree((t) => appendTo(t, null, cond('text', '')))}>+ Bedingung</button>}
          {open && <button className="linkbtn" onClick={() => setTree((t) => appendTo(t, null, { op: 'or', items: [cond('person', null), cond('person', null)] }))}>+ Gruppe (ODER)</button>}
          <button className="linkbtn" onClick={onSave} disabled={!text}>Suche merken</button>
          <button className="linkbtn" onClick={() => { setTree({ op: 'and', items: [] }); setError(null) }}>Alles zurücksetzen</button>
        </div>
      </div>
      <div id="fbody" hidden={!open}>
        {tree.items.length === 0
          ? <div className="emptyq">Noch keine Bedingungen. Tippen Sie oben einen Begriff, wählen Sie links einen Filter oder fügen Sie eine Bedingung hinzu.</div>
          : <GroupView group={tree} top setTree={setTree} />}
        <textarea ref={area} className="syntax" id="syntax" rows={1} spellCheck={false} aria-label="Suchformel als Text"
          value={draft} onFocus={() => setEditing(true)} onBlur={() => setEditing(false)} onChange={(e) => setDraft(e.target.value)}
          placeholder='z. B. ort:Gorleben jahr:1977-1981 -Polizei' />
      </div>
      {error && <div className="syntax-err" role="alert">{error}</div>}
    </div>
  )
}

function GroupView({ group, top, setTree }: { group: Group; top?: boolean; setTree: Props['setTree'] }) {
  return (
    <div className={`group${top ? '' : ' sub'}${group.not ? ' neg' : ''}`}>
      {!top && (
        <div className="ghead">
          <button className="notbtn" aria-pressed={!!group.not} onClick={() => setTree((t) => updateNode(t, group._id!, { not: !group.not }))}>NICHT</button>
          <span className="label">Gruppe</span>
          <button className="linkbtn" onClick={() => setTree((t) => appendTo(t, group._id!, cond('text', '')))}>+ Bedingung</button>
          <button className="x" title="Gruppe entfernen" aria-label="Gruppe entfernen" onClick={() => setTree((t) => removeNode(t, group._id!))}>×</button>
        </div>
      )}
      {group.items.map((item, idx) => (
        <div className="row" key={item._id}>
          <span className="op">
            {idx === 0 ? (top ? 'Finde' : '') : (
              <select aria-label="Verknüpfung" value={group.op} onChange={(e) => setTree((t) => updateNode(t, group._id!, { op: e.target.value as 'and' | 'or' }))}>
                <option value="and">UND</option>
                <option value="or">ODER</option>
              </select>
            )}
          </span>
          {isGroup(item)
            ? <div style={{ flex: 1, minWidth: 0 }}><GroupView group={item} setTree={setTree} /></div>
            : isRaw(item)
              ? <RawRow node={item} setTree={setTree} />
              : <CondRow c={item} setTree={setTree} />}
        </div>
      ))}
    </div>
  )
}

function RawRow({ node, setTree }: { node: Node & { easydb: Record<string, unknown> }; setTree: Props['setTree'] }) {
  return (
    <>
      <button className="notbtn" aria-pressed={!!node.not} onClick={() => setTree((t) => updateNode(t, node._id!, { not: !node.not }))}>NICHT</button>
      <span className="label">easydb</span>
      <code className="rawel">{JSON.stringify(node.easydb)}</code>
      <button className="x" aria-label="Entfernen" onClick={() => setTree((t) => removeNode(t, node._id!))}>×</button>
    </>
  )
}

function CondRow({ c, setTree }: { c: Condition; setTree: Props['setTree'] }) {
  const isLink = LINK_FIELDS.has(c.field)
  const isDate = DATE_FIELDS.has(c.field)
  const [input, setInput] = useState(valueText(c))
  const [sugg, setSugg] = useState<Suggestion[]>([])
  const [focus, setFocus] = useState(false)

  useEffect(() => { if (!focus) setInput(valueText(c)) }, [c, focus])

  useEffect(() => {
    if (!isLink || !focus || input.trim().length < 2) { setSugg([]); return }
    const ctrl = new AbortController()
    const h = setTimeout(() => {
      api.suggest(input.trim(), ctrl.signal).then((r) => setSugg(r.filter((s) => s.field === c.field).slice(0, 8))).catch(() => undefined)
    }, 150)
    return () => { clearTimeout(h); ctrl.abort() }
  }, [input, focus, isLink, c.field])

  function commit(v: string) {
    if (isDate) {
      const m = v.trim().match(/^(\d{4})?\s*(-|–)?\s*(\d{4})?$/)
      if (!m || (!m[1] && !m[3])) { setTree((t) => updateNode(t, c._id!, { value: v.trim() || null, label: null })); return }
      const range = m[1] && !m[2] ? { from: +m[1], to: +m[1] } : { ...(m[1] ? { from: +m[1] } : {}), ...(m[3] ? { to: +m[3] } : {}) }
      setTree((t) => updateNode(t, c._id!, { value: range, label: null }))
    } else if (isLink) {
      // free text: the server resolves the name to easydb ids
      if (v.trim() !== valueText(c)) setTree((t) => updateNode(t, c._id!, { value: v.trim() || null, label: v.trim() || null }))
    } else {
      setTree((t) => updateNode(t, c._id!, { value: v, label: null }))
    }
  }

  const resolved = isLink && typeof c.value === 'number'
  const ph = c.field === 'text' ? 'Begriff oder "Phrase"' : isDate ? 'z. B. 1977-1981' : c.field === 'inv' ? 'z. B. SGZ-1-F-2-8376' : isLink ? 'Name eingeben …' : 'Wert'

  return (
    <>
      <button className="notbtn" aria-pressed={!!c.not} title="Bedingung ausschließen" onClick={() => setTree((t) => updateNode(t, c._id!, { not: !c.not }))}>NICHT</button>
      <select className="fsel" aria-label="Feld" value={c.field} onChange={(e) => setTree((t) => updateNode(t, c._id!, { field: e.target.value, value: null, label: null }))}>
        {BUILDER_FIELDS.concat(BUILDER_FIELDS.includes(c.field) ? [] : [c.field]).map((f) => <option key={f} value={f}>{FIELD_LABELS[f] ?? f}</option>)}
      </select>
      <span className="valwrap">
        <input className={`val${resolved ? ' resolved' : ''}`} id={`val-${c._id}`} value={input} placeholder={ph} aria-label="Wert"
          title={resolved ? `easydb-ID ${c.value}` : undefined}
          onFocus={() => setFocus(true)}
          onBlur={() => { setTimeout(() => setFocus(false), 150); commit(input) }}
          onChange={(e) => { setInput(e.target.value); if (!isLink && !isDate) setTree((t) => updateNode(t, c._id!, { value: e.target.value })) }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(input) } }} />
        {focus && sugg.length > 0 && (
          <div className="suggest" role="listbox">
            {sugg.map((s) => (
              <button key={`${s.field}${s.id}`} type="button" role="option" aria-selected="false"
                onMouseDown={(e) => { e.preventDefault(); setInput(s.label); setTree((t) => updateNode(t, c._id!, { value: s.id, label: s.label })); setFocus(false) }}>
                <span>{s.label}</span><span className="path">{s.path.join(' › ')}{s.count != null && ` · ${s.count}`}</span>
              </button>
            ))}
          </div>
        )}
      </span>
      <button className="x" title="Bedingung entfernen" aria-label="Bedingung entfernen" onClick={() => setTree((t) => removeNode(t, c._id!))}>×</button>
    </>
  )
}
